/**
 * LLM Gateway client (Sprint 1, 2026-05-20)
 *
 * Shared SSE client cho cluster 6 AI Tools (botgpt, chatgpt, mindmap,
 * translation, prompt_engineer, document). Purge `REACT_APP_OPENAI_KEY`
 * leak khỏi frontend bundle — mọi LLM call đi qua server-side proxy.
 *
 * Endpoint: POST /api/v1/llm-gateway/chat/stream (SSE)
 *   - event: start  → {tool, model}
 *   - event: text   → {delta: '...'}
 *   - event: done   → {usage: {cost_usd}}
 *   - event: error  → {detail}
 *
 * Auth: JWT từ localStorage.auth_token (Authorization: Bearer ...).
 *       KHÔNG còn user_token query param (legacy đã clean Jan 2026).
 *
 * Rate limit: backend trả 429 với body
 *   {detail: {error, tool, limit, reset_in_seconds}}
 * Client throw `RateLimitError` để component hiển thị snackbar.
 */

import { authManager } from 'utils/authManager';

const ALLOWED_TOOLS = new Set([
    'botgpt',
    'chatgpt',
    'mindmap',
    'translation',
    'prompt_engineer',
    'document',
]);

/** Resolve base URL từ env (REACT_APP_API_URL) — fallback '/api/v1' khi prod (Apache proxy). */
function gatewayUrl() {
    const base = process.env.REACT_APP_API_URL || '/api/v1';
    return `${base}/llm-gateway/chat/stream`;
}

function authHeader() {
    const t = authManager.getToken();
    return t ? `Bearer ${t}` : '';
}

export class RateLimitError extends Error {
    constructor({ tool, limit, resetIn }) {
        super(`Quota cho tool ${tool} đã hết. Thử lại sau ${resetIn || 60}s.`);
        this.name = 'RateLimitError';
        this.tool = tool;
        this.limit = limit;
        this.resetIn = resetIn;
    }
}

/**
 * Stream chat qua LLM gateway. Trả về Promise resolve khi SSE done.
 *
 * @param {Object}   opts
 * @param {string}   opts.tool          - 'botgpt' | 'chatgpt' | ...
 * @param {Array}    opts.messages      - [{role:'user'|'assistant', content:'...'}]
 * @param {string}   [opts.systemPrompt]
 * @param {number}   [opts.temperature=0.7]
 * @param {number}   [opts.maxTokens=2000]
 * @param {AbortSignal} [opts.signal]   - cleanup khi component unmount
 * @param {Function} [opts.onText]      - (delta: string) => void
 * @param {Function} [opts.onStart]     - ({tool, model}) => void
 * @param {Function} [opts.onDone]      - (payload) => void
 * @param {Function} [opts.onError]     - (errObj) => void
 * @param {string}   [opts.model]       - DEPRECATED, ignored (warn nếu pass)
 */
export async function streamChat({
    tool,
    messages,
    systemPrompt,
    temperature = 0.7,
    maxTokens = 2000,
    signal,
    onText,
    onStart,
    onDone,
    onError,
    model,
}) {
    if (!ALLOWED_TOOLS.has(tool)) {
        throw new Error(`Unknown tool '${tool}'. Allowed: ${[...ALLOWED_TOOLS].join(', ')}`);
    }
    if (model) {
        // Backward-compat: gateway tự routing model → tool. Warn dev nếu vẫn pass hardcoded.
        // eslint-disable-next-line no-console
        console.warn(
            `[llmGateway] hardcoded model='${model}' bị bỏ qua — backend route theo tool='${tool}'.`
        );
    }

    const headers = {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
    };
    const auth = authHeader();
    if (auth) headers.Authorization = auth;

    const res = await fetch(gatewayUrl(), {
        method: 'POST',
        headers,
        signal,
        body: JSON.stringify({
            tool,
            messages,
            system_prompt: systemPrompt,
            temperature,
            max_tokens: maxTokens,
        }),
    });

    if (res.status === 429) {
        // Backend trả {detail: {error, tool, limit, reset_in_seconds}}
        let payload = null;
        try {
            payload = await res.json();
        } catch {
            /* ignore */
        }
        const d = payload?.detail || {};
        const err = new RateLimitError({
            tool: d.tool || tool,
            limit: d.limit,
            resetIn: d.reset_in_seconds,
        });
        onError?.({ status: 429, ...d, message: err.message });
        throw err;
    }
    if (!res.ok) {
        const txt = await res.text().catch(() => '');
        const err = new Error(`LLM Gateway HTTP ${res.status}: ${txt.slice(0, 200)}`);
        onError?.({ status: res.status, detail: txt, message: err.message });
        throw err;
    }
    if (!res.body) throw new Error('LLM Gateway: empty response body');

    const reader = res.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buf = '';

    try {
        while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buf += decoder.decode(value, { stream: true });
            // SSE frames are separated by blank line (\n\n)
            while (true) {
                const idx = buf.indexOf('\n\n');
                if (idx < 0) break;
                const block = buf.slice(0, idx);
                buf = buf.slice(idx + 2);
                let event = 'message';
                let data = null;
                for (const line of block.split('\n')) {
                    if (line.startsWith('event:')) event = line.slice(6).trim();
                    else if (line.startsWith('data:')) {
                        const raw = line.slice(5).trim();
                        try {
                            data = JSON.parse(raw);
                        } catch {
                            data = raw;
                        }
                    }
                }
                if (event === 'start') onStart?.(data || {});
                else if (event === 'text') onText?.(data?.delta || '');
                else if (event === 'done') {
                    onDone?.(data || {});
                    return data || {};
                } else if (event === 'error') {
                    onError?.(data || {});
                    throw new Error(data?.detail || 'LLM Gateway stream error');
                }
            }
        }
    } catch (e) {
        if (e?.name === 'AbortError') return null;
        throw e;
    }
    return null;
}

/**
 * Adapter giữ chữ ký cũ `onEvent(MessageEvent)` để 6 component cũ chưa migrate
 * vẫn dùng được mà không phải sửa logic render. MessageEvent.data là JSON string.
 *
 * Format compat với pattern cũ (line `data: {"answer": "..."}`):
 *   - delta chunk → onEvent({data: JSON.stringify({answer: delta, type:'stream'})})
 *   - done       → onEvent({data: JSON.stringify({type:'end', usage})})
 *   - error      → onEvent({data: JSON.stringify({type:'error', detail})})
 */
export function streamChatLegacy({ tool, question, history = [], systemPrompt, signal, onEvent, model }) {
    const messages = [];
    for (const h of history) {
        if (h?.prompt) messages.push({ role: 'user', content: h.prompt });
        if (h?.response) messages.push({ role: 'assistant', content: h.response });
    }
    messages.push({ role: 'user', content: question });

    const emit = (obj) => {
        try {
            onEvent?.(new MessageEvent('message', { data: JSON.stringify(obj) }));
        } catch {
            /* swallow */
        }
    };

    return streamChat({
        tool,
        messages,
        systemPrompt,
        signal,
        model,
        onStart: (s) => emit({ type: 'start', ...s }),
        onText: (delta) => emit({ answer: delta, type: 'stream' }),
        onDone: (d) => emit({ type: 'end', ...(d || {}) }),
        onError: (e) => emit({ type: 'error', ...(e || {}) }),
    });
}

export const TOOLS = [...ALLOWED_TOOLS];
